import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { Area2D, ChangePolygonPoints, MoveCameraPath, MoveCustomBuilding, MoveFence, MoveGrid, MoveHedge, MoveLine, MoveLineHill, MoveRoad, MoveSpline, MoveSplineHill, MoveRectangle, MoveRectangleHill, MoveCircle, MoveCircleHill, MoveText, ScaleBackgroundImage, ScaleArena } from './tools';
import { AbstractInteractors } from './AbstractInteractors';
import { EditRoomElement } from './../cmd/EditRoomElement';
import { InteractorMode, AddEnding } from './../RoomManager';
import { Util } from '../utils/Util';
import { InteractorType } from '@egr/wcf/modules/core/tool';
import { ElementChangedFlags, SceneElement } from '@egr/wcf/modules/core/mdl';
import { clamp, PI_DIV2 } from '@egr/wcf/modules/utils/math';
import { getRotationQuaternion } from '@egr/wcf/modules/core/utils/MeshUtils';
import { perpendicularVector2, VECTOR3_X, VECTOR3_Y, VECTOR3_Z, signedAngleBetweenVectors2, VECTOR2_X, perpendicularVector3 } from '@egr/wcf/modules/core/utils/MathUtils';
export class RoomElementInteractors extends AbstractInteractors {
    constructor(pRoomManager, pRoomElement) {
        super(pRoomManager);
        this.onCameraChanged = () => {
            if (this.mbillboardInteractors.length === 0) {
                return;
            }
            const tviewcamDir = Util.to2d(this.mApp.viewer.view.cameraControl.direction.clone().negate());
            const tviewAngle = signedAngleBetweenVectors2(tviewcamDir, this.mV2_down);
            let t3dRotationX = getRotationQuaternion(this.mRoomElement.node).clone();
            t3dRotationX.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_Y, tviewAngle));
            for (let tInteractor of this.mbillboardInteractors) {
                tInteractor.transform3d = true;
                tInteractor.transform3dRotation = t3dRotationX;
            }
        };
        this.onCameraModeChanged = () => {
            if (this.mbillboardInteractors.length === 0) {
                return;
            }
            const tviewcamDir = Util.to2d(this.mApp.viewer.view.cameraControl.direction.clone().negate());
            const tviewAngle = signedAngleBetweenVectors2(tviewcamDir, this.mV2_down);
            let t3dRotationX = getRotationQuaternion(this.mRoomElement.node).clone();
            t3dRotationX.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_Y, tviewAngle));
            for (let tInteractor of this.mbillboardInteractors) {
                tInteractor.transform3d = true;
                tInteractor.transform3dRotation = t3dRotationX;
            }
        };
        this.mRoomElement = pRoomElement;
        this.mDistInteractors = [null, null, null, null];
        this.mHideInteractors = new Array();
        this.mbillboardInteractors = new Array();
        this.mV2_down = new Vector2(0, -1);
        this.mView = pRoomManager.view;
        this.mMCB = pRoomManager.app.theme.mainColorBright;
        if (this.mRoomManager.view != null) {
            this.mRoomManager.view.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
            this.mRoomManager.view.cameraControl.eventCameraModeChanged.addListener(this.onCameraModeChanged);
        }
    }
    dispose() {
        if (this.mRoomManager.view != null) {
            this.mRoomManager.view.cameraControl.eventCameraChanged.removeListener(this.onCameraChanged);
        }
        this.mHideInteractors = [];
        this.mbillboardInteractors = [];
    }
    // tslint:disable-next-line:cyclomatic-complexity
    showInteractors(pShow, pOnlyLength = false) {
        this.clear();
        if (((!pShow || this.mApp.model.selection.length !== 1) && (!pOnlyLength)) || this.mView == null) {
            return;
        }
        this.mInteractorManager.enabled = true;
        let tDir = this.mRoomElement.getEdge(1, false).subtract(this.mRoomElement.getEdge(0, false)).normalize();
        let tDirP = perpendicularVector2(tDir);
        let t3dRotationX = getRotationQuaternion(this.mRoomElement.node).clone();
        t3dRotationX.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        let t3dRotationX2 = getRotationQuaternion(this.mRoomElement.node).clone();
        t3dRotationX2.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        let tgeoType = this.mRoomElement.geometry.getType();
        // tslint:disable-next-line:switch-default
        switch (tgeoType) {
            case "Spline":
                this.showInteractorsSpline(t3dRotationX, tDirP, pShow);
                break;
            case "Rectangle":
                this.showInteractorsRectangle(t3dRotationX, t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "Circle":
                this.showInteractorsCircle(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "Line":
                this.showInteractorsLine(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "PillarC":
                this.showInteractorsPillar(t3dRotationX, tDirP);
                break;
            case "PillarB":
                this.showInteractorsPillar(t3dRotationX, tDirP);
                break;
            case "BackgroundImage":
                this.showInteractorsBackgroundImage(t3dRotationX, t3dRotationX2, tDirP, pShow);
                break;
            case "Text":
                this.showInteractorsText(t3dRotationX, t3dRotationX2, tDirP, pShow);
                break;
            case "CustomBuilding":
                this.showInteractorsCustomBuilding(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "Road":
                this.showInteractorsRoad(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "Hedge":
                this.showInteractorsHedge(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "Fence":
                this.showInteractorsFence(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "Sphere":
                this.showInteractorsSphere(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "SafetyZone":
                this.showInteractorsSafetyZone(t3dRotationX, tDirP, pShow, pOnlyLength);
                break;
            case "CameraPath":
                this.showInteractorsCamPath(t3dRotationX, tDirP, pShow);
                break;
            case "Arena":
                this.showInteractorsArena(t3dRotationX, tDirP, pShow);
                break;
            case "Grid":
                this.showInteractorsGrid(t3dRotationX, pShow);
                break;
            case "GeoMarker":
                this.showInteractorsGeoMarker(pShow);
                break;
            default:
                break;
        }
    }
    clear() {
        this.mInteractorManager.clear();
        this.mInteractorManager.enabled = false;
        this.mDistInteractors = [null, null, null, null];
        this.mHideInteractors = [];
        this.mbillboardInteractors = [];
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _updateDistInteractors() {
        this.mInteractorManager.enabled = true;
        let t3dRotation = getRotationQuaternion(this.mRoomElement.node).clone();
        t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        if (this.mRoomElement.wall != null) {
            let tWall = this.mRoomElement.wall;
            let tDir = tWall.endPoint.subtract(tWall.startPoint).normalize();
            let tDirP = perpendicularVector2(tDir);
            for (let i = 0; i < 4; i++) {
                let tDist = this.mRoomElement._getDistanceEdge(i);
                let tPos = this.getDistTextPos(i, tDist);
                let tOffset = i >= 2 ? -15 : 15;
                let tInteractor = this.createTextInteractor(Util.to3d(tPos), this.lengthToString(tDist), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), tOffset, t3dRotation, false, this.mDistInteractors[i]);
                tInteractor.onTriggerAction = () => this.showInputField(RoomElementInteractors.INPUT_DIST, tDist, tInteractor);
                tInteractor._edge = i;
                tInteractor.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractor.element.firstChild);
                tInteractor.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractor.element.firstChild);
                this.mDistInteractors[i] = tInteractor;
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    showLineLengthInteractors(p3dRotation) {
        // no length interactors when drawing line based on grid
        if (this.mRoomElement.GridRE_REF != null) {
            return;
        }
        let tElement = this.mRoomElement.geometry;
        this.mInteractorManager.enabled = true;
        let tLines = tElement.getLines();
        let tPoints = tElement.getPoints();
        let tOffset = 15;
        if (tLines.length === 1)
            tOffset = 25;
        else
            tOffset = 15;
        if (this.mRoomManager.interactorState === InteractorMode.AddDelete)
            tOffset += 5;
        for (let i = 0; i < tLines.length; i++) {
            let tP1 = Vector3.TransformCoordinates(tLines[i][0], this.mRoomElement.transform);
            let tP2 = Vector3.TransformCoordinates(tLines[i][1], this.mRoomElement.transform);
            let tDir = Util.to2d(tP2.subtract(tP1));
            let tDirP = perpendicularVector2(tDir);
            let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
            let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
            t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //line length
            let tSideCenter = Vector2.Center(Util.to2d(tP2), Util.to2d(tP1));
            let tSideLengthL = Vector2.Distance(Util.to2d(tP2), Util.to2d(tP1));
            if (tSideLengthL < 0.001)
                return;
            let tInteractorL = this.createTextInteractor(Util.to3d(tSideCenter), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), tOffset, t3dRotation, false, null);
            tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_LENGTH + "_" + i, tSideLengthL, tInteractorL);
            tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
            tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
        }
        if (this.mRoomManager.viewMode !== 0)
            return;
        // start point distance
        // tslint:disable-next-line:no-collapsible-if
        if (this.mRoomManager.interactorState === InteractorMode.AddDelete) {
            if (tPoints.length >= 4) {
                let tP1 = Vector3.TransformCoordinates(tPoints[0], this.mRoomElement.transform);
                for (let i = 2; i < tPoints.length - 1; i++) {
                    let tP2 = Vector3.TransformCoordinates(tPoints[i], this.mRoomElement.transform);
                    let tDir = Util.to2d(tP2.subtract(tP1));
                    let tDirP = perpendicularVector2(tDir);
                    let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
                    let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
                    t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
                    //line length
                    let tlineDist = Vector3.Distance(tP1, tP2);
                    let ttextPos = ((tP2.subtract(tP1)).normalize()).scale(tlineDist * 0.1);
                    ttextPos = tP2.subtract(ttextPos);
                    let tSideLengthL = Vector2.Distance(Util.to2d(tP2), Util.to2d(tP1));
                    let tInteractorL = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos)), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), 10, t3dRotation, false, null);
                    tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_STARTPOINT + "_" + i, tSideLengthL, tInteractorL);
                    tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
                    tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
                }
            }
        }
        if (this.mRoomManager.viewMode === 0 && this.mRoomElement.crossHairVisible) {
            let tRefPoints = this.mRoomElement.RefPointIndices;
            let tP1 = Vector3.TransformCoordinates(tPoints[tRefPoints.P1], this.mRoomElement.transform);
            let tP11 = Vector3.TransformCoordinates(tPoints[tRefPoints.P2], this.mRoomElement.transform);
            let tPT = Vector3.TransformCoordinates(this.mRoomElement.RefPoint, this.mRoomElement.transform);
            let tDir = Util.to2d(tPT.subtract(tP1));
            let tDirP = perpendicularVector2(tDir);
            let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
            let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
            t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //ref point length 1
            let tlineDist1 = Vector3.Distance(tP1, tPT);
            let ttextPos1 = ((tPT.subtract(tP1)).normalize()).scale(tlineDist1 * 0.15);
            ttextPos1 = tPT.subtract(ttextPos1);
            let tSideLengthL = Vector2.Distance(Util.to2d(tPT), Util.to2d(tP1));
            let tInteractorL = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos1)), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), 10, t3dRotation, false, null, false, "wcf-room text-interactorFIX");
            tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_REFPOINT1 + "_-1", tSideLengthL, tInteractorL);
            tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
            tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
            let tPT11 = Vector3.TransformCoordinates(this.mRoomElement.RefPoint, this.mRoomElement.transform);
            let tDir11 = Util.to2d(tPT11.subtract(tP11));
            let tDirP11 = perpendicularVector2(tDir11);
            let tAngle11 = signedAngleBetweenVectors2(VECTOR2_X, tDir11);
            let t3dRotation11 = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle11);
            t3dRotation11.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //ref point length 2
            let tlineDist11 = Vector3.Distance(tP11, tPT11);
            let ttextPos11 = ((tPT11.subtract(tP11)).normalize()).scale(tlineDist11 * 0.15);
            ttextPos11 = tPT11.subtract(ttextPos11);
            let tSideLengthL11 = Vector2.Distance(Util.to2d(tPT11), Util.to2d(tP11));
            let tInteractorL11 = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos11)), this.lengthToString(tSideLengthL11) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir11).normalize(), Util.to3d(tDirP11).normalize(), 10, t3dRotation11, false, null);
            tInteractorL11.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_REFPOINT2 + "_-2", tSideLengthL11, tInteractorL11);
            tInteractorL11.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL11.element.firstChild);
            tInteractorL11.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL11.element.firstChild);
        }
    }
    showLengthInteractors(pType) {
        let tElement = null;
        switch (pType) {
            case "Fence":
                tElement = this.mRoomElement.geometry;
                break;
            case "Hedge":
                tElement = this.mRoomElement.geometry;
                break;
            case "Road":
                tElement = this.mRoomElement.geometry;
                break;
            default:
                break;
        }
        if (tElement == null)
            return;
        this.mInteractorManager.enabled = true;
        let tLines = tElement.getLines();
        //line middle points
        let tmidPoints = tElement.getMidPoints();
        let tsegLength = tElement.getSegmentsLength();
        let tOffset = 25;
        let tmP = null;
        let tmTP = null;
        for (let i = 0; i < tLines.length; i++) {
            let tP1 = Vector3.TransformCoordinates(tLines[i][0], this.mRoomElement.transform);
            let tP2 = Vector3.TransformCoordinates(tLines[i][1], this.mRoomElement.transform);
            let tDir = Util.to2d(tP2.subtract(tP1));
            let tDirP = perpendicularVector2(tDir);
            let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
            let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
            t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            if (tmidPoints[i] == null ||
                tmidPoints[i].x === undefined || tmidPoints[i].y === undefined || tmidPoints[i].z === undefined) {
                continue;
            }
            let tLenPos = Vector3.TransformCoordinates(tmidPoints[i], this.mRoomElement.transform);
            //line length
            let tSideCenter = Vector2.Center(Util.to2d(tP2), Util.to2d(tP1));
            let tSideLengthL = tsegLength[i];
            let tInteractorL = this.createTextInteractor(tLenPos, this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), tOffset, t3dRotation, false, null);
            tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
            tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
        }
    }
    showSplineHelpLinesInteractors(p3dRotation) {
        if (this.mRoomManager.viewMode !== 0)
            return;
        let tElement = this.mRoomElement.geometry;
        this.mInteractorManager.enabled = true;
        let tPoints = tElement.getPoints();
        // start point distance
        // tslint:disable-next-line:no-collapsible-if
        if (this.mRoomManager.interactorState === InteractorMode.AddDelete) {
            if (tPoints.length >= 4) {
                let tP1 = Vector3.TransformCoordinates(tPoints[0], this.mRoomElement.transform);
                for (let i = 1; i < tPoints.length; i++) {
                    let tP2 = Vector3.TransformCoordinates(tPoints[i], this.mRoomElement.transform);
                    let tDir = Util.to2d(tP2.subtract(tP1));
                    let tDirP = perpendicularVector2(tDir);
                    let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
                    let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
                    t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
                    //line length
                    let tlineDist = Vector3.Distance(tP1, tP2);
                    let ttextPos = ((tP2.subtract(tP1)).normalize()).scale(tlineDist * 0.1);
                    ttextPos = tP2.subtract(ttextPos);
                    let tSideCenter = Vector2.Center(Util.to2d(tP2), Util.to2d(tP1));
                    let tSideLengthL = Vector2.Distance(Util.to2d(tP2), Util.to2d(tP1));
                    let tInteractorL = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos)), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), 10, t3dRotation, false, null);
                    tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_STARTPOINT + "_" + i, tSideLengthL, tInteractorL);
                    tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
                    tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
                }
            }
        }
    }
    showRoadLengthInteractors() {
        let tElement = this.mRoomElement.geometry;
        this.mInteractorManager.enabled = true;
        let tLines = tElement.getLines();
        let tOffset = 15;
        if (tLines.length === 1)
            tOffset = 25;
        else
            tOffset = 15;
        for (let i = 0; i < tLines.length; i++) {
            let tP1 = Util.to3d(Util.to2d(Vector3.TransformCoordinates(tLines[i][0], this.mRoomElement.transform)));
            let tP2 = Util.to3d(Util.to2d(Vector3.TransformCoordinates(tLines[i][1], this.mRoomElement.transform)));
            let tDir = Util.to2d(tP2.subtract(tP1));
            let tDirP = perpendicularVector2(tDir);
            let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
            let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
            t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //line length
            let tSideCenter = Vector2.Center(Util.to2d(tP2), Util.to2d(tP1));
            let tSideLengthL = Vector2.Distance(Util.to2d(tP2), Util.to2d(tP1));
            let tInteractorL = this.createTextInteractor(Util.to3d(tSideCenter), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), tOffset, t3dRotation, false, null);
            tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_LENGTH + "_" + i, tSideLengthL, tInteractorL);
            tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
            tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
        }
    }
    showCustomBuildingLengthInteractors() {
        let tElement = this.mRoomElement.geometry;
        this.mInteractorManager.enabled = true;
        let tLines = tElement.getLines();
        let tOffset = 15;
        if (tLines.length === 1)
            tOffset = 25;
        else
            tOffset = 15;
        for (let i = 0; i < tLines.length; i++) {
            let tP1 = Util.to3d(Util.to2d(Vector3.TransformCoordinates(tLines[i][0], this.mRoomElement.transform)));
            let tP2 = Util.to3d(Util.to2d(Vector3.TransformCoordinates(tLines[i][1], this.mRoomElement.transform)));
            let tDir = Util.to2d(tP2.subtract(tP1));
            let tDirP = perpendicularVector2(tDir);
            let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
            let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
            t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //line length
            let tSideCenter = Vector2.Center(Util.to2d(tP2), Util.to2d(tP1));
            let tSideLengthL = Vector2.Distance(Util.to2d(tP2), Util.to2d(tP1));
            let tInteractorL = this.createTextInteractor(Util.to3d(tSideCenter), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), tOffset, t3dRotation, false, null);
            tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.BUILDING_LENGTH + "_" + i, tSideLengthL, tInteractorL);
            tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
            tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
        }
    }
    showCircleRadiusInteractor() {
        this.mInteractorManager.enabled = true;
        let tWM = this.mRoomElement.node.getWorldMatrix();
        let tDP = new Vector3(this.mRoomElement.width * 0.25, 0, 0);
        tDP = Vector3.TransformCoordinates(tDP, tWM);
        let tSideLengthL = this.mRoomElement.width * 0.5;
        let tlengthStr = this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit);
        let tDir = new Vector3(1, 0, 0).normalize();
        let tDirP = perpendicularVector3(tDir).normalize();
        let tAngle = signedAngleBetweenVectors2(VECTOR2_X, Util.to2d(tDir));
        let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
        if (this.mRoomElement.node.rotationQuaternion != null)
            t3dRotation = this.mRoomElement.node.rotationQuaternion.clone();
        t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        let tInteractorL = this.createTextInteractor(tDP, tlengthStr, tDir, tDirP, -14, t3dRotation, false, null, true);
        tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.CIRCLE_RADIUS, tSideLengthL, tInteractorL);
        tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
        tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
    }
    showRectangleSideInteractor(p3dRotationX) {
        let tElement = this.mRoomElement.geometry;
        this.mInteractorManager.enabled = true;
        let tnodeRot = this.mRoomElement.node.rotationQuaternion;
        let tPoints = tElement.getPoints();
        if (tPoints.length < 4)
            return;
        let tP1 = Vector3.TransformCoordinates(tPoints[0], this.mRoomElement.transform);
        let tP2 = Vector3.TransformCoordinates(tPoints[1], this.mRoomElement.transform);
        let tP3 = Vector3.TransformCoordinates(tPoints[3], this.mRoomElement.transform);
        //side interactor
        let tDirT = tP2.subtract(tP1);
        //let tDirPT = utils.math.perpendicularVector3(tDirT);
        let tDirPT = new Vector3(-1, 0, 0);
        let tAngleT = signedAngleBetweenVectors2(VECTOR2_X, Util.to2d(tDirT));
        let t3dRotationT = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngleT);
        t3dRotationT.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        //top interactor
        let tDirS = tP3.subtract(tP1);
        //let tDirPS = utils.math.perpendicularVector3(tDirS);
        let tDirPS = new Vector3(0, 0, 1);
        let tAngleS = signedAngleBetweenVectors2(VECTOR2_X, Util.to2d(tDirS));
        let t3dRotationS = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngleS);
        t3dRotationS.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        //side
        let tSideCenterT = Vector3.Center(tP2, tP1);
        let tSideLengthLT = Vector3.Distance(tP2, tP1);
        let tInteractorLT = this.createTextInteractor(tSideCenterT, this.lengthToString(tSideLengthLT) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), tDirT.normalize(), tDirPT.normalize(), 14, t3dRotationT, false, null, true);
        tInteractorLT.onTriggerAction = () => this.showInputField(RoomElementInteractors.RECTANGLE_SIDE, tSideLengthLT, tInteractorLT);
        tInteractorLT.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorLT.element.firstChild);
        tInteractorLT.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorLT.element.firstChild);
        //top
        let tSideCenterS = Vector3.Center(tP3, tP1);
        let tSideLengthLS = Vector3.Distance(tP3, tP1);
        let tInteractorLS = this.createTextInteractor(tSideCenterS, this.lengthToString(tSideLengthLS) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), tDirS.normalize(), tDirPS.normalize(), 14, t3dRotationS, false, null, true);
        tInteractorLS.onTriggerAction = () => this.showInputField(RoomElementInteractors.RECTANGLE_TOP, tSideLengthLS, tInteractorLS);
        tInteractorLS.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorLS.element.firstChild);
        tInteractorLS.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorLS.element.firstChild);
        // ref point interactors
        if (this.mRoomManager.viewMode !== 0)
            return;
        if (this.mRoomManager.viewMode === 0 && this.mRoomElement.crossHairVisible) {
            let tRefPoints = this.mRoomElement.RefPointIndices;
            let tP11 = Vector3.TransformCoordinates(tPoints[tRefPoints.P1], this.mRoomElement.transform);
            let tP12 = Vector3.TransformCoordinates(tPoints[tRefPoints.P2], this.mRoomElement.transform);
            let tPT = Vector3.TransformCoordinates(this.mRoomElement.RefPoint, this.mRoomElement.transform);
            let tDir = Util.to2d(tPT.subtract(tP11));
            let tDirP = perpendicularVector2(tDir);
            let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
            let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
            t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //ref point length 1
            let tlineDist1 = Vector3.Distance(tP11, tPT);
            let ttextPos1 = ((tPT.subtract(tP11)).normalize()).scale(tlineDist1 * 0.15);
            ttextPos1 = tPT.subtract(ttextPos1);
            let tSideLengthL = Vector2.Distance(Util.to2d(tPT), Util.to2d(tP11));
            let tInteractorL = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos1)), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), 10, t3dRotation, false, null, false, "wcf-room text-interactorFIX");
            tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_REFPOINT1 + "_-1", tSideLengthL, tInteractorL);
            tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
            tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
            let tPT11 = Vector3.TransformCoordinates(this.mRoomElement.RefPoint, this.mRoomElement.transform);
            let tDir11 = Util.to2d(tPT11.subtract(tP12));
            let tDirP11 = perpendicularVector2(tDir11);
            let tAngle11 = signedAngleBetweenVectors2(VECTOR2_X, tDir11);
            let t3dRotation11 = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle11);
            t3dRotation11.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //ref point length 2
            let tlineDist11 = Vector3.Distance(tP12, tPT11);
            let ttextPos11 = ((tPT11.subtract(tP12)).normalize()).scale(tlineDist11 * 0.15);
            ttextPos11 = tPT11.subtract(ttextPos11);
            let tSideLengthL11 = Vector2.Distance(Util.to2d(tPT11), Util.to2d(tP12));
            let tInteractorL11 = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos11)), this.lengthToString(tSideLengthL11) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir11).normalize(), Util.to3d(tDirP11).normalize(), 10, t3dRotation11, false, null);
            tInteractorL11.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_REFPOINT2 + "_-2", tSideLengthL11, tInteractorL11);
            tInteractorL11.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL11.element.firstChild);
            tInteractorL11.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL11.element.firstChild);
        }
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    // tslint:disable-next-line:cyclomatic-complexity
    handleInputField(pId, pOldLength, pNewLength, pInteractor) {
        if (pNewLength < 0.01 && this.mRoomElement.type !== "Arena") {
            return;
        }
        const tRE = this.mRoomElement;
        let tRefPointIdx = this.mRoomElement.RefPointIndices;
        let tPos = pId.indexOf("_");
        let tgeoType = "";
        let tIdx = 0;
        if (tPos > -1) {
            tgeoType = pId.substring(0, tPos);
            tIdx = parseInt(pId.substring(tPos + 1, pId.length), undefined);
        }
        else {
            tgeoType = pId;
        }
        switch (tgeoType) {
            case RoomElementInteractors.LINE_LENGTH:
                let tElement = this.mRoomElement.geometry;
                let tPoints = tElement.getPoints();
                if (this.mRoomManager.interactorState === InteractorMode.AddDelete && (tIdx === tPoints.length - 1)) {
                    let tdistPrev = Vector3.Distance(tPoints[tIdx], tPoints[tIdx - 1]);
                    let tcircleIntersect = Util.intersect2Circles(tPoints[tIdx - 1], tdistPrev, tPoints[0], pNewLength);
                    if (tcircleIntersect.P1 !== false || tcircleIntersect.P2 !== false) {
                        let tDist1 = 1000000000;
                        let tDist2 = 1000000000;
                        if (tcircleIntersect.P1 !== false) {
                            tDist1 = Vector3.Distance(tPoints[tIdx], tcircleIntersect.P1);
                        }
                        if (tcircleIntersect.P2 !== false) {
                            tDist2 = Vector3.Distance(tPoints[tIdx], tcircleIntersect.P2);
                        }
                        if (tDist1 <= tDist2) {
                            tPoints[tIdx] = tcircleIntersect.P1;
                        }
                        else {
                            tPoints[tIdx] = tcircleIntersect.P2;
                        }
                    }
                    else {
                        let tLine_SP = tPoints[tIdx].subtract(tPoints[0]);
                        tLine_SP.normalize();
                        tLine_SP = tLine_SP.scale(pNewLength);
                        tPoints[tIdx] = tPoints[0].add(tLine_SP);
                    }
                    let tnewPoints = new Array();
                    for (let tP of tPoints) {
                        tnewPoints.push(tP);
                    }
                    tElement.setPoints(tnewPoints);
                }
                else {
                    let tLine = new Vector3(0, 0, 0);
                    if (tIdx === tPoints.length - 1) {
                        tLine = tPoints[tIdx].subtract(tPoints[0]);
                    }
                    else {
                        tLine = tPoints[tIdx + 1].subtract(tPoints[tIdx]);
                    }
                    tLine.normalize();
                    tLine = tLine.scale(pNewLength);
                    if (tIdx === tPoints.length - 1) {
                        tPoints[tIdx] = tPoints[0].add(tLine);
                    }
                    else {
                        tPoints[tIdx + 1] = tPoints[tIdx].add(tLine);
                    }
                    let tnewPoints = new Array();
                    for (let tP of tPoints) {
                        tnewPoints.push(tP);
                    }
                    tElement.setPoints(tnewPoints);
                }
                this.mRoomElement.updateGeometry(false);
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.BUILDING_LENGTH:
                let tElementB = this.mRoomElement.geometry;
                let tPointsB = tElementB.getPoints();
                let tLine1 = new Vector3(0, 0, 0);
                if (tIdx === tPointsB.length - 2) {
                    tLine1 = tPointsB[tIdx].subtract(tPointsB[0]);
                }
                else {
                    tLine1 = tPointsB[tIdx].subtract(tPointsB[tIdx + 1]);
                }
                tLine1.normalize();
                tLine1 = tLine1.scale(pNewLength);
                if (tIdx === tPointsB.length - 2) {
                    tPointsB[tIdx] = tPointsB[0].add(tLine1);
                }
                else {
                    tPointsB[tIdx] = tPointsB[tIdx + 1].add(tLine1);
                    if (tIdx === 0) {
                        tPointsB[tPointsB.length - 1] = tPointsB[0].clone();
                    }
                }
                let tnewPoints1 = new Array();
                for (let tP of tPointsB) {
                    tnewPoints1.push(tP);
                }
                tElementB.setPoints(tnewPoints1);
                this.mRoomElement.updateGeometry(false);
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.LINE_STARTPOINT:
                let tElement_Line = this.mRoomElement.geometry;
                let tPoints_SP = tElement_Line.getPoints();
                let tdistPrev1 = Vector3.Distance(tPoints_SP[tIdx], tPoints_SP[tIdx - 1]);
                let tcircleIntersect1 = Util.intersect2Circles(tPoints_SP[tIdx - 1], tdistPrev1, tPoints_SP[0], pNewLength);
                if (tcircleIntersect1.P1 !== false || tcircleIntersect1.P2 !== false) {
                    let tDist1 = 1000000000;
                    let tDist2 = 1000000000;
                    if (tcircleIntersect1.P1 !== false) {
                        tDist1 = Vector3.Distance(tPoints_SP[tIdx], tcircleIntersect1.P1);
                    }
                    if (tcircleIntersect1.P2 !== false) {
                        tDist2 = Vector3.Distance(tPoints_SP[tIdx], tcircleIntersect1.P2);
                    }
                    if (tDist1 <= tDist2) {
                        tPoints_SP[tIdx] = tcircleIntersect1.P1;
                    }
                    else {
                        tPoints_SP[tIdx] = tcircleIntersect1.P2;
                    }
                }
                else {
                    let tLine_SP = tPoints_SP[tIdx].subtract(tPoints_SP[0]);
                    tLine_SP.normalize();
                    tLine_SP = tLine_SP.scale(pNewLength);
                    tPoints_SP[tIdx] = tPoints_SP[0].add(tLine_SP);
                }
                let tnewPoints_SP = new Array();
                for (let tP of tPoints_SP) {
                    tnewPoints_SP.push(tP);
                }
                tElement_Line.setPoints(tnewPoints_SP);
                this.mRoomElement.updateGeometry(false);
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.LINE_REFPOINT1:
                let tElementRef1 = this.mRoomElement.geometry;
                let tPointsRef1 = tElementRef1.getPoints();
                let tLineRef1 = this.mRoomElement.RefPoint.subtract(tPointsRef1[tRefPointIdx.P1]);
                tLineRef1.normalize();
                tLineRef1 = tLineRef1.scale(pNewLength);
                let tnewRefPoint1 = tPointsRef1[tRefPointIdx.P1].add(tLineRef1);
                this.mRoomElement.setRefPoint(tnewRefPoint1);
                this.mRoomElement.updateGeometry(false);
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.LINE_REFPOINT2:
                let tElementRef2 = this.mRoomElement.geometry;
                let tPointsRef2 = tElementRef2.getPoints();
                let tLineRef2 = this.mRoomElement.RefPoint.subtract(tPointsRef2[tRefPointIdx.P2]);
                tLineRef2.normalize();
                tLineRef2 = tLineRef2.scale(pNewLength);
                let tnewRefPoint2 = tPointsRef2[tRefPointIdx.P2].add(tLineRef2);
                this.mRoomElement.setRefPoint(tnewRefPoint2);
                this.mRoomElement.updateGeometry(false);
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.CIRCLE_RADIUS:
                let tCircle = this.mRoomElement.geometry;
                tCircle.setNewRadius(pNewLength);
                this.mRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
                this.mRoomElement.notifyChange(ElementChangedFlags.Properties);
                this.mRoomElement.notifyChange(ElementChangedFlags.BoundingBox);
                this.mRoomElement.updateGeometry();
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.RECTANGLE_SIDE:
                let tREside = this.mRoomElement.geometry;
                tREside.setNewDepth(pNewLength);
                this.mRoomElement.updateGeometry();
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.RECTANGLE_TOP:
                let tREtop = this.mRoomElement.geometry;
                tREtop.setNewWidth(pNewLength);
                this.mRoomElement.updateGeometry();
                this.mRoomManager.cutOverlappingShapes();
                break;
            case RoomElementInteractors.ARENA_FIELD_LENGTH:
                const tcurrFieldLength = tRE.Params.arenaFieldLength * tRE.Params.arenaLaneScale;
                const tnewArenaFieldScale = clamp(pNewLength / tcurrFieldLength, 0.01, 50);
                tRE.Params.arenaFieldScale = tnewArenaFieldScale;
                this.mRoomElement.updateGeometry();
                this.mRoomManager.cutOverlappingShapes();
                break;
            default:
                break;
        }
        this.mRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
        this.mRoomElement.notifyChange(ElementChangedFlags.Properties);
        this.mRoomElement.notifyChange(ElementChangedFlags.BoundingBox);
        this.mRoomManager.contentChanged();
        if (pId === RoomElementInteractors.INPUT_DIST)
            this.setDistanceEdge(pInteractor._edge, pOldLength, pNewLength);
    }
    _mouseEnter(pRoomManager, pSpanElement) {
        let tStyle = pSpanElement.style;
        tStyle.color = pRoomManager.app.theme.mainColorBright;
    }
    _mouseLeave(pRoomManager, pSpanElement) {
        let tStyle = pSpanElement.style;
        tStyle.color = "#4f4f4f";
    }
    showInteractorsSpline(p3dRotation, pDirP, pShow) {
        let tElement = this.mRoomElement.geometry;
        let tPoints = tElement.getPoints().slice();
        //line middle points
        let tmidPoints = tElement.getMidPoints();
        // hill top moving point
        let thillTop = this.mRoomElement.Params.hillTopOffset;
        thillTop = Vector3.TransformCoordinates(thillTop, this.mRoomElement.transform);
        if (pShow) {
            //move spline points
            for (let i = 0; i < tPoints.length; i++) {
                let tP = tPoints[i];
                let tmP = tmidPoints[i];
                let tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                let tmTP = Vector3.TransformCoordinates(tmP, this.mRoomElement.transform);
                let tInteractor = null;
                switch (this.mRoomManager.interactorState) {
                    case InteractorMode.Default:
                        tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                        tInteractor.onTriggerAction = () => this.movePointSpline(MoveSpline.Mode.MovePoint, i);
                        break;
                    case InteractorMode.AddDelete:
                        tInteractor = this.createInteractor("deletePoint-interactor", tTP, null, 0, p3dRotation, false, true);
                        tInteractor.onTriggerAction = () => this.deletePolygonPoint(i);
                        let tInteractorAdd = this.createInteractor("plus-interactor", tmTP, null, 0, p3dRotation, false, true);
                        tInteractorAdd.onTriggerAction = () => this.addPolygonPoint(i + 1);
                        break;
                    default:
                        tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                        tInteractor.onTriggerAction = () => this.movePointSpline(MoveSpline.Mode.MovePoint, i);
                        break;
                }
            }
            if (this.mRoomManager.viewMode === 0 && this.mRoomElement.crossHairVisible) {
                //triangle ref point
                let tP = this.mRoomElement.RefPoint;
                let tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                let tInteractor = null;
                tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                tInteractor.onTriggerAction = () => this.movePointSpline(MoveSpline.Mode.MoveRefPoint, 0);
                tInteractor.visible = this.mRoomManager.viewMode === 0 ? true : false;
                this.mHideInteractors.push(tInteractor);
            }
            if (this.mRoomElement.Params.terrainType !== "Flat") {
                thillTop.y = this.mRoomElement.Params.hillHeight;
                let tInteractor_Bottom = this.createInteractor("move-interactor", thillTop, null, 0, p3dRotation, false, true);
                tInteractor_Bottom.onTriggerAction = () => this.moveSplineHill();
            }
        }
        this.showSplineRefPointLengthInteractors(p3dRotation);
        this.showSplineHelpLinesInteractors(p3dRotation);
    }
    showSplineRefPointLengthInteractors(p3dRotation) {
        let tElement = this.mRoomElement.geometry;
        this.mInteractorManager.enabled = true;
        let tPoints = tElement.getPoints();
        if (this.mRoomManager.viewMode !== 0)
            return;
        if (this.mRoomManager.viewMode === 0 && this.mRoomElement.crossHairVisible) {
            let tRefPoints = this.mRoomElement.RefPointIndices;
            let tP1 = Vector3.TransformCoordinates(tPoints[tRefPoints.P1], this.mRoomElement.transform);
            let tP11 = Vector3.TransformCoordinates(tPoints[tRefPoints.P2], this.mRoomElement.transform);
            let tPT = Vector3.TransformCoordinates(this.mRoomElement.RefPoint, this.mRoomElement.transform);
            let tDir = Util.to2d(tPT.subtract(tP1));
            let tDirP = perpendicularVector2(tDir);
            let tAngle = signedAngleBetweenVectors2(VECTOR2_X, tDir);
            let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
            t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //ref point length 1
            let tlineDist1 = Vector3.Distance(tP1, tPT);
            let ttextPos1 = ((tPT.subtract(tP1)).normalize()).scale(tlineDist1 * 0.15);
            ttextPos1 = tPT.subtract(ttextPos1);
            let tSideLengthL = Vector2.Distance(Util.to2d(tPT), Util.to2d(tP1));
            let tInteractorL = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos1)), this.lengthToString(tSideLengthL) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), 10, t3dRotation, false, null, false, "wcf-room text-interactorFIX");
            tInteractorL.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_REFPOINT1 + "_-1", tSideLengthL, tInteractorL);
            tInteractorL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL.element.firstChild);
            tInteractorL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL.element.firstChild);
            let tPT11 = Vector3.TransformCoordinates(this.mRoomElement.RefPoint, this.mRoomElement.transform);
            let tDir11 = Util.to2d(tPT11.subtract(tP11));
            let tDirP11 = perpendicularVector2(tDir11);
            let tAngle11 = signedAngleBetweenVectors2(VECTOR2_X, tDir11);
            let t3dRotation11 = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle11);
            t3dRotation11.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
            //ref point length 2
            let tlineDist11 = Vector3.Distance(tP11, tPT11);
            let ttextPos11 = ((tPT11.subtract(tP11)).normalize()).scale(tlineDist11 * 0.15);
            ttextPos11 = tPT11.subtract(ttextPos11);
            let tSideLengthL11 = Vector2.Distance(Util.to2d(tPT11), Util.to2d(tP11));
            let tInteractorL11 = this.createTextInteractor(Util.to3d(Util.to2d(ttextPos11)), this.lengthToString(tSideLengthL11) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir11).normalize(), Util.to3d(tDirP11).normalize(), 10, t3dRotation11, false, null);
            tInteractorL11.onTriggerAction = () => this.showInputField(RoomElementInteractors.LINE_REFPOINT2 + "_-2", tSideLengthL11, tInteractorL11);
            tInteractorL11.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorL11.element.firstChild);
            tInteractorL11.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorL11.element.firstChild);
        }
    }
    showInteractorsLine(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tElement = this.mRoomElement.geometry;
        let tPoints = tElement.getPoints().slice();
        let tmidPoints = tElement.getMidPoints();
        let tWM = this.mRoomElement.node.getWorldMatrix();
        // hill top moving point
        let thillTop = this.mRoomElement.Params.hillTopOffset;
        thillTop = Vector3.TransformCoordinates(thillTop, tWM);
        if (pOnlyLength) {
            this.showLineLengthInteractors(p3dRotation);
        }
        else {
            if (pShow) {
                //move line points
                for (let i = 0; i < tPoints.length; i++) {
                    let tP = tPoints[i];
                    let tmP = tmidPoints[i];
                    let tTP = Vector3.TransformCoordinates(tP, tWM);
                    let tmTP = Vector3.TransformCoordinates(tmP, tWM);
                    let tInteractor = null;
                    switch (this.mRoomManager.interactorState) {
                        case InteractorMode.Default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                            tInteractor.onTriggerAction = () => this.movePointLine(MoveLine.Mode.MovePoint, i);
                            break;
                        case InteractorMode.AddDelete:
                            tInteractor = this.createInteractor("deletePoint-interactor", tTP, null, 0, p3dRotation, true, true);
                            tInteractor.onTriggerAction = () => this.deletePolygonPoint(i);
                            let tInteractorAdd = this.createInteractor("plus-interactor", tmTP, null, 0, p3dRotation, true, true);
                            tInteractorAdd.onTriggerAction = () => this.addPolygonPoint(i + 1);
                            break;
                        default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                            tInteractor.onTriggerAction = () => this.movePointLine(MoveLine.Mode.MovePoint, i);
                            break;
                    }
                }
                if (this.mRoomManager.viewMode === 0 && this.mRoomElement.crossHairVisible) {
                    //triangle ref point
                    let tP = this.mRoomElement.RefPoint;
                    let tTP = Vector3.TransformCoordinates(tP, tWM);
                    let tInteractor = null;
                    tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                    tInteractor.onTriggerAction = () => this.movePointLine(MoveLine.Mode.MoveRefPoint, 0);
                    tInteractor.visible = this.mRoomManager.viewMode === 0 ? true : false;
                    this.mHideInteractors.push(tInteractor);
                }
            }
            if (this.mRoomElement.Params.terrainType !== "Flat") {
                thillTop.y = this.mRoomElement.Params.hillHeight;
                let tInteractor_Bottom = this.createInteractor("move-interactor", thillTop, null, 0, p3dRotation, false, true);
                tInteractor_Bottom.onTriggerAction = () => this.moveLineHill();
            }
            this.showLineLengthInteractors(p3dRotation);
        }
    }
    showInteractorsRoad(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tElement = this.mRoomElement.geometry;
        let tPoints = tElement.getPoints().slice();
        let tmidPoints = tElement.getMidPoints();
        if (pOnlyLength) {
            this.showLengthInteractors("Road");
        }
        else {
            if (pShow) {
                //move road points
                let tmP = null;
                let tmTP = null;
                for (let i = 0; i < tPoints.length; i++) {
                    let tP = tPoints[i];
                    if (i < tmidPoints.length)
                        tmP = tmidPoints[i];
                    else
                        tmP = null;
                    let tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                    if (tmP !== null)
                        tmTP = Vector3.TransformCoordinates(tmP, this.mRoomElement.transform);
                    let tInteractor = null;
                    switch (this.mRoomManager.interactorState) {
                        case InteractorMode.Default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation);
                            tInteractor.onTriggerAction = () => this.movePointRoad(MoveRoad.Mode.MovePoint, i);
                            break;
                        case InteractorMode.AddDelete:
                            tInteractor = this.createInteractor("deletePoint-interactor", tTP, null, 0, p3dRotation, true, true);
                            tInteractor.onTriggerAction = () => this.deletePolygonPoint(i);
                            if (tmP !== null && tmTP !== null) {
                                let tInteractorAdd = this.createInteractor("plus-interactor", tmTP, null, 0, p3dRotation, true, true);
                                tInteractorAdd.onTriggerAction = () => this.addPolygonPoint(i + 1);
                            }
                            break;
                        default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                            tInteractor.onTriggerAction = () => this.movePointLine(MoveLine.Mode.MovePoint, i);
                            break;
                    }
                }
            }
            this.showLengthInteractors("Road");
        }
    }
    showInteractorsHedge(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tElement = this.mRoomElement.geometry;
        let tPoints = tElement.getPoints().slice();
        let tmidPoints = tElement.getMidPoints();
        if (pOnlyLength) {
            this.showLengthInteractors("Hedge");
        }
        else {
            if (pShow) {
                //move hedge points
                let tmP = null;
                let tmTP = null;
                for (let i = 0; i < tPoints.length; i++) {
                    let tP = tPoints[i];
                    if (i < tmidPoints.length)
                        tmP = tmidPoints[i];
                    else
                        tmP = null;
                    let tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                    if (tmP !== null)
                        tmTP = Vector3.TransformCoordinates(tmP, this.mRoomElement.transform);
                    let tInteractor = null;
                    switch (this.mRoomManager.interactorState) {
                        case InteractorMode.Default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation);
                            tInteractor.onTriggerAction = () => this.movePointHedge(MoveHedge.Mode.MovePoint, i);
                            break;
                        case InteractorMode.AddDelete:
                            tInteractor = this.createInteractor("deletePoint-interactor", tTP, null, 0, p3dRotation, true, true);
                            tInteractor.onTriggerAction = () => this.deletePolygonPoint(i);
                            if (tmP !== null && tmTP !== null) {
                                let tInteractorAdd = this.createInteractor("plus-interactor", tmTP, null, 0, p3dRotation, true, true);
                                tInteractorAdd.onTriggerAction = () => this.addPolygonPoint(i + 1);
                            }
                            break;
                        default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                            tInteractor.onTriggerAction = () => this.movePointLine(MoveLine.Mode.MovePoint, i);
                            break;
                    }
                }
            }
            this.showLengthInteractors("Hedge");
        }
    }
    showInteractorsFence(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tElement = this.mRoomElement.geometry;
        let tPoints = tElement.getPoints().slice();
        let tmidPoints = tElement.getMidPoints();
        if (pOnlyLength) {
            this.showLengthInteractors("Fence");
        }
        else {
            if (pShow) {
                //move fence points
                let tmP = null;
                let tmTP = null;
                for (let i = 0; i < tPoints.length; i++) {
                    let tP = tPoints[i];
                    if (i < tmidPoints.length)
                        tmP = tmidPoints[i];
                    else
                        tmP = null;
                    let tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                    if (tmP !== null)
                        tmTP = Vector3.TransformCoordinates(tmP, this.mRoomElement.transform);
                    let tInteractor = null;
                    switch (this.mRoomManager.interactorState) {
                        case InteractorMode.Default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation);
                            tInteractor.onTriggerAction = () => this.movePointFence(MoveFence.Mode.MovePoint, i);
                            break;
                        case InteractorMode.AddDelete:
                            tInteractor = this.createInteractor("deletePoint-interactor", tTP, null, 0, p3dRotation, true, true);
                            tInteractor.onTriggerAction = () => this.deletePolygonPoint(i);
                            if (tmP !== null && tmTP !== null) {
                                let tInteractorAdd = this.createInteractor("plus-interactor", tmTP, null, 0, p3dRotation, true, true);
                                tInteractorAdd.onTriggerAction = () => this.addPolygonPoint(i + 1);
                            }
                            break;
                        default:
                            tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true);
                            tInteractor.onTriggerAction = () => this.movePointFence(MoveFence.Mode.MovePoint, i);
                            break;
                    }
                }
            }
            this.showLengthInteractors("Fence");
        }
    }
    showInteractorsRectangle(p3dRotationX, p3dRotationX2, pDirP, pShow, pOnlyLength) {
        let tElement = this.mRoomElement.geometry;
        // hill top moving point
        let thillTop = this.mRoomElement.Params.hillTopOffset;
        thillTop = Vector3.TransformCoordinates(thillTop, this.mRoomElement.transform);
        if (pOnlyLength) {
            this.showRectangleSideInteractor(p3dRotationX);
        }
        else {
            let tPoints = tElement.getPoints().slice();
            if (pShow) {
                //move rectangle points
                for (let i = 0; i < tPoints.length; i++) {
                    let tP = tPoints[i];
                    let tTP = tP;
                    tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                    let tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotationX, false, true);
                    tInteractor.onTriggerAction = () => this.movePointRectangle(MoveRectangle.Mode.MovePoint, i);
                }
                if (this.mRoomManager.viewMode === 0 && this.mRoomElement.crossHairVisible) {
                    //triangle ref point
                    let tP = this.mRoomElement.RefPoint;
                    let tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                    let tInteractor = null;
                    tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotationX, false, true);
                    tInteractor.onTriggerAction = () => this.movePointLine(MoveLine.Mode.MoveRefPoint, 0);
                    tInteractor.visible = this.mRoomManager.viewMode === 0 ? true : false;
                    this.mHideInteractors.push(tInteractor);
                }
            }
            if (this.mRoomElement.Params.terrainType !== "Flat") {
                thillTop.y = this.mRoomElement.Params.hillHeight;
                let tInteractor_Bottom = this.createInteractor("move-interactor", thillTop, null, 0, p3dRotationX, false, true);
                tInteractor_Bottom.onTriggerAction = () => this.moveRectangleHill();
            }
            this.showRectangleSideInteractor(p3dRotationX);
        }
    }
    showInteractorsCircle(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tWM = this.mRoomElement.node.getWorldMatrix();
        let tDP = new Vector3(this.mRoomElement.width * 0.5, 0, 0);
        let thillTop = this.mRoomElement.Params.hillTopOffset;
        tDP = Vector3.TransformCoordinates(tDP, tWM);
        thillTop = Vector3.TransformCoordinates(thillTop, tWM);
        if (pOnlyLength) {
            this.showCircleRadiusInteractor();
        }
        else {
            if (pShow) {
                //change radius/diameter
                let tInteractor = this.createInteractor("dot-interactor", tDP, null, 0, p3dRotation, false, true);
                tInteractor.onTriggerAction = () => this.movePointCircle(MoveCircle.Mode.MovePoint, 1);
                if (this.mRoomElement.Params.terrainType !== "Flat") {
                    thillTop.y = this.mRoomElement.Params.hillHeight;
                    let tInteractor_Bottom = this.createInteractor("move-interactor", thillTop, null, 0, p3dRotation, false, true);
                    tInteractor_Bottom.onTriggerAction = () => this.moveCircleHill();
                }
            }
            this.showCircleRadiusInteractor();
        }
    }
    showInteractorsSphere(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tNodePos = this.mRoomElement.node.position.clone();
        let tWM = this.mRoomElement.node.getWorldMatrix();
        let tDP = new Vector3(this.mRoomElement.width * 0.5, 0, 0);
        tDP = Vector3.TransformCoordinates(tDP, tWM);
        if (pOnlyLength) {
            this.showCircleRadiusInteractor();
        }
        else {
            if (pShow) {
                //change radius/diameter
                let tInteractor = this.createInteractor("dot-interactor", tDP, null, 0, p3dRotation, false, true);
                tInteractor.onTriggerAction = () => this.movePointCircle(MoveCircle.Mode.MovePoint, 1);
                //change position
                let tMoveInteractor = this.createInteractor("move-interactor", tNodePos, null, 0, p3dRotation, false, true);
                tMoveInteractor.onTriggerAction = () => this.movePointCircle(MoveCircle.Mode.Move_Circle, 0);
            }
            this.showCircleRadiusInteractor();
        }
    }
    showInteractorsSafetyZone(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tNodePos = this.mRoomElement.node.absolutePosition.clone();
        let tWM = this.mRoomElement.node.getWorldMatrix();
        let tDP = new Vector3(this.mRoomElement.width * 0.5, 0, 0);
        tDP = Vector3.TransformCoordinates(tDP, tWM);
        if (pShow) {
            //change position
            let tSZvisibilityInteractor = this.createInteractor("safetyzone-interactor", tNodePos, null, 0, p3dRotation, false, true);
            tSZvisibilityInteractor.type = InteractorType.Button;
            tSZvisibilityInteractor.onTriggerAction = () => this.toggleSZVisibility();
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    showInteractorsCamPath(p3dRotation, pDirP, pShow) {
        let tElement = this.mRoomElement.geometry;
        if (!pShow || tElement == null) {
            return;
        }
        let tBoxPos = tElement.getBoxPos(true);
        tBoxPos = Vector3.TransformCoordinates(tBoxPos, this.mRoomElement.transform);
        const tcurrCustomTarget = tElement.getCurrCustomTarget();
        const tcamPathPos = this.mRoomElement.Params.CamPathPoints.slice();
        const tviewcamDir = Util.to2d(this.mApp.viewer.view.cameraControl.direction.clone().negate());
        const tviewAngle = signedAngleBetweenVectors2(tviewcamDir, this.mV2_down);
        const tPathDir2d = tElement.getCurrPathDirection(true);
        const tPathDir2dperp = Util.to3d(perpendicularVector2(tPathDir2d));
        const tcurrPathDirAngle = tElement.getCurrPathRad() - PI_DIV2;
        const tccPos = this.mView.cameraControl.position.clone();
        const tdirToCam = (tccPos.subtract(tBoxPos)).normalize();
        let tviewToPathRot = p3dRotation.multiply(Quaternion.RotationAxis(VECTOR3_Z, tcurrPathDirAngle));
        tviewToPathRot = tviewToPathRot.multiply(Quaternion.RotationAxis(tdirToCam, 0));
        let t3dRotationX = getRotationQuaternion(this.mRoomElement.node).clone();
        t3dRotationX.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_Y, tviewAngle));
        //line middle points
        let tmidPoints = tElement.getMidPoints();
        let tTargetPos = tElement.getTargetPos();
        const tcustomTargetPos = tElement.getCustomTargetPos();
        let tPoints = tElement.getPoints().slice();
        //move camera path points
        for (let i = 0; i < tPoints.length; i++) {
            let tP = tPoints[i];
            let tmP = Vector3.Zero();
            let tmTP = Vector3.Zero();
            // not a closed path !
            if (i < tPoints.length - 1) {
                tmP = tmidPoints[i];
                tmTP = Vector3.TransformCoordinates(tmP, this.mRoomElement.transform);
            }
            let tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
            let tInteractor = null;
            switch (this.mRoomManager.interactorState_CameraPath) {
                case InteractorMode.Default:
                    tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true, undefined, this.mMCB);
                    tInteractor.onTriggerAction = () => this.moveCameraPathPoint(MoveCameraPath.Mode.MovePoint, i);
                    break;
                case InteractorMode.AddDelete:
                    tInteractor = this.createInteractor("deletePoint-interactor", tTP, null, 0, p3dRotation, false, true);
                    tInteractor.onTriggerAction = () => this.deletePolygonPoint(i);
                    const thOffset = (tTP.clone()).add(new Vector3(0, 0.4, 0));
                    let tIntHeightOffset = this.createInteractor("moveHeight-interactor", tTP, Vector3.Up(), 40, t3dRotationX, false, true, undefined, "#005BA1");
                    tIntHeightOffset.onTriggerAction = () => this.movePolygonPoint_Height(i);
                    this.mbillboardInteractors.push(tIntHeightOffset);
                    if (i < tPoints.length - 1) {
                        let tInteractorAdd = this.createInteractor("plus-interactor", tmTP, null, 0, p3dRotation, false, true);
                        tInteractorAdd.onTriggerAction = () => this.addPolygonPoint(i);
                    }
                    break;
                case InteractorMode.Target:
                    //
                    break;
                default:
                    tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation, false, true, undefined, "#005BA1");
                    tInteractor.onTriggerAction = () => this.moveCameraPathPoint(MoveCameraPath.Mode.MovePoint, i);
                    break;
            }
        }
        if (this.mRoomManager.interactorState_CameraPath === InteractorMode.Target) {
            if (!tElement.camIsOnCustomTarget()) {
                let tInt_addCustomTarget = this.createInteractor("plus-interactor", tBoxPos, tPathDir2dperp, 35, null, false, true, undefined, "#95C11F");
                tInt_addCustomTarget.type = InteractorType.Button;
                tInt_addCustomTarget.onTriggerAction = () => this.mRoomManager.addCustomCamTarget();
            }
            else {
                let tInt_addCustomTarget = this.createInteractor("deletePoint-interactor", tBoxPos, tPathDir2dperp, 35, null, false, true, undefined, "#95C11F");
                tInt_addCustomTarget.type = InteractorType.Button;
                tInt_addCustomTarget.onTriggerAction = () => this.mRoomManager.deleteCustomCamTarget();
                if (tcurrCustomTarget != null && !tcurrCustomTarget.viewAlongPath) {
                    let tIntAlongPath = this.createInteractor("viewAlongPath-interactor", tBoxPos, tPathDir2dperp, -35, tviewToPathRot, false, true, undefined, "#95C11F");
                    tIntAlongPath.type = InteractorType.Button;
                    tIntAlongPath.onTriggerAction = () => this.mRoomManager.CustomCamTarget_viewAlong();
                }
            }
            // show custom target points
            for (let tIdx of tcustomTargetPos) {
                let tP = tcamPathPos[tIdx].clone();
                tP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
                let tInteractor = null;
                tInteractor = this.createInteractor("dot-interactor", tP, null, 0, p3dRotation, false, true, undefined, "#95C11F");
                tInteractor.type = InteractorType.Button;
                tInteractor.onTriggerAction = () => this.moveCameraBox2Point(tIdx);
            }
        }
        let tInteractorBoxPos = this.createInteractor("cammove-interactor", tBoxPos, null, 0, null, false, true);
        tInteractorBoxPos.onTriggerAction = () => this.moveCameraBox(MoveCameraPath.Mode.MoveBox, -1);
        //this.mbillboardInteractors.push(tInteractorBoxPos);
        // target / eye interactor
        const tnextViewAlongPath = tElement.nextViewAlongPath();
        let tcamEyeColor = "#95C11F";
        if (tnextViewAlongPath == null ||
            (tnextViewAlongPath != null && !tnextViewAlongPath) ||
            (this.mRoomManager.interactorState_CameraPath != InteractorMode.Target)) {
            tcamEyeColor = "#b2b2b2";
        }
        let tInt_camTarget = this.createInteractor("eye-interactor", tTargetPos, null, 0, p3dRotation, false, true, undefined, tcamEyeColor);
        if (this.mRoomManager.interactorState_CameraPath === InteractorMode.Target) {
            tInt_camTarget.onTriggerAction = () => this.moveCameraView(MoveCameraPath.Mode.MoveCamView, -1);
        }
        // const tcurrCustomTarget = tElement.getCurrCustomTarget();
        if (tcurrCustomTarget != null && tcurrCustomTarget.viewAlongPath !== true) {
            let tIntHeightTargetOffset = this.createInteractor("moveHeight-interactor", tTargetPos, Vector3.Up(), 40, t3dRotationX, false, true, undefined, "#95C11F");
            tIntHeightTargetOffset.onTriggerAction = () => this.movePolygonPoint_TargetHeight(tcurrCustomTarget);
            this.mbillboardInteractors.push(tIntHeightTargetOffset);
        }
        this.showExtendPathInteractors(p3dRotation);
        // this.showViewAlongPathInteractor(p3dRotation);
    }
    showInteractorsArena(p3dRotation, pDirP, pShow) {
        const tRE = this.mRoomElement;
        let tWM = this.mRoomElement.node.getWorldMatrix();
        let tscalePos = new Vector3(this.mRoomElement.width * 0.5, 0, -this.mRoomElement.depth * 0.5);
        tscalePos = Vector3.TransformCoordinates(tscalePos, tWM);
        let tlengthPos = new Vector3(this.mRoomElement.width * 0.5, 0, 0);
        tlengthPos = Vector3.TransformCoordinates(tlengthPos, tWM);
        if (pShow) {
            // change arena scale
            let tInt_Scale = this.createInteractor("dot-interactor", tscalePos, null, 0, p3dRotation, false, true);
            tInt_Scale.onTriggerAction = () => this.scaleArena(ScaleArena.Mode.ScaleComplete);
            // change radius/diameter
            if (!this.mRoomElement.Params.arenaCircle) {
                let tInt_Length = this.createInteractor("dot-interactor", tlengthPos, null, 0, p3dRotation, false, true);
                tInt_Length.onTriggerAction = () => this.scaleArena(ScaleArena.Mode.ScaleLength);
            }
        }
        if (!this.mRoomElement.Params.arenaCircle) {
            this.showAreaLengthInteractor(tRE);
        }
    }
    showInteractorsGrid(p3dRotationX, pShow) {
        if (!pShow) {
            return;
        }
        let tElement = this.mRoomElement.geometry;
        let tPoints = tElement.getPoints().slice();
        //move grid points
        for (let i = 0; i < tPoints.length; i++) {
            let tP = tPoints[i];
            let tTP = tP;
            tTP = Vector3.TransformCoordinates(tP, this.mRoomElement.transform);
            if (i === 2) {
                let tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotationX, false, true, undefined, '#0000ff');
                tInteractor.onTriggerAction = () => this.movePointGrid(MoveGrid.Mode.MovePoint, i);
                if (this.mRoomElement.REforGrid_REF != null) {
                    let tLineToolInteractor = this.createInteractor("linetool-interactor", tTP, Vector3.Right(), 30, p3dRotationX, false, true, undefined, undefined);
                    tLineToolInteractor.type = InteractorType.Button;
                    tLineToolInteractor.onTriggerAction = () => this.startTool_Line();
                }
            }
        }
    }
    showInteractorsGeoMarker(pShow) {
        const tview = this.mView;
        if (tview == null ||
            !pShow ||
            tview.cameraControl.cameraMode !== 0) {
            return;
        }
        let tpos = this.mRoomElement.position.clone();
        let tDir = new Vector3(1, 0, 0).normalize();
        let tDirP = perpendicularVector3(tDir).normalize();
        let t3dRotation = getRotationQuaternion(this.mRoomElement.node).clone();
        t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        const tgeoPos = this.mRoomElement.geoPosition;
        const tLat = tgeoPos.latitude.toFixed(5);
        const tLong = tgeoPos.longitude.toFixed(5);
        const tText = tLat + ', ' + tLong;
        let tintGeoPosition = this.createTextInteractor(tpos, tText, tDir, tDirP, 70, t3dRotation, false, null, true);
        if (this.mRoomElement.onTriggerAction != null) {
            tintGeoPosition.onTriggerAction = () => this.mRoomElement.onTriggerAction();
        }
        else {
            tintGeoPosition.onTriggerAction = () => this.showInputFieldText(RoomElementInteractors.ARENA_FIELD_LENGTH, tText, tintGeoPosition);
        }
        tintGeoPosition.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tintGeoPosition.element.firstChild);
        tintGeoPosition.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tintGeoPosition.element.firstChild);
    }
    startTool_Line() {
        this.mApp.tools.startTool(new Area2D(this.mRoomManager, "Line"));
    }
    scaleArena(pMode) {
        let tTool = new ScaleArena(this.mRoomManager);
        tTool.initialize(this.mRoomElement, pMode);
        this.mApp.tools.startTool(tTool);
    }
    showAreaLengthInteractor(pRE) {
        this.mInteractorManager.enabled = true;
        const tsegRadius = pRE.Params.arenaSegRadius * pRE.Params.arenaLaneScale;
        let tWM = this.mRoomElement.node.getWorldMatrix();
        let tintPos = new Vector3(0, 0, tsegRadius);
        tintPos = Vector3.TransformCoordinates(tintPos, tWM);
        let tfieldLength = pRE.Params.arenaFieldLength * pRE.Params.arenaLaneScale * pRE.Params.arenaFieldScale;
        let tlengthStr = this.lengthToString(tfieldLength) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit);
        let tDir = new Vector3(1, 0, 0).normalize();
        let tDirP = perpendicularVector3(tDir).normalize();
        let tAngle = signedAngleBetweenVectors2(VECTOR2_X, Util.to2d(tDir));
        let t3dRotation = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngle);
        if (this.mRoomElement.node.rotationQuaternion != null)
            t3dRotation = this.mRoomElement.node.rotationQuaternion.clone();
        t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        let tIntFL = this.createTextInteractor(tintPos, tlengthStr, tDir, tDirP, -14, t3dRotation, false, null, true);
        tIntFL.onTriggerAction = () => this.showInputField(RoomElementInteractors.ARENA_FIELD_LENGTH, tfieldLength, tIntFL);
        tIntFL.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tIntFL.element.firstChild);
        tIntFL.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tIntFL.element.firstChild);
    }
    showExtendPathInteractors(p3dRotation) {
        let tElement = this.mRoomElement.geometry;
        const tcamPathPos = this.mRoomElement.Params.CamPathPoints.slice();
        if (tElement == null || tcamPathPos.length < 2 || this.mRoomManager.interactorState_CameraPath !== InteractorMode.AddDelete) {
            return;
        }
        const tlastIdx = tcamPathPos.length - 1;
        let tStart = tcamPathPos[0];
        tStart = Vector3.TransformCoordinates(tStart, this.mRoomElement.transform);
        const tStartDir = tElement.getStartDirection();
        let tEnd = tcamPathPos[tlastIdx];
        tEnd = Vector3.TransformCoordinates(tEnd, this.mRoomElement.transform);
        const tEndDir = tElement.getEndDirection();
        let tIntAddStart = this.createInteractor("plus-interactor", tStart, tStartDir, 50, p3dRotation, false, true);
        tIntAddStart.onTriggerAction = () => this.extendCamPath(AddEnding.Start);
        let tIntAddEnd = this.createInteractor("plus-interactor", tEnd, tEndDir, 50, p3dRotation, false, true);
        tIntAddEnd.onTriggerAction = () => this.extendCamPath(AddEnding.End);
    }
    showViewAlongPathInteractor(p3dRotation) {
        let tElement = this.mRoomElement.geometry;
        const tcurrCustomTarget = tElement.getCurrCustomTarget();
        const tcurrPathDirAngle = tElement.getCurrPathRad() - PI_DIV2;
        let tBoxPos = tElement.getBoxPos(true);
        tBoxPos = Vector3.TransformCoordinates(tBoxPos, this.mRoomElement.transform);
        const tPathDir2d = tElement.getCurrPathDirection(true);
        const tPathDir2dperp = Util.to3d(perpendicularVector2(tPathDir2d));
        const tccPos = this.mView.cameraControl.position.clone();
        const tdirToCam = (tccPos.subtract(tBoxPos)).normalize();
        let t3dRotation = p3dRotation.multiply(Quaternion.RotationAxis(VECTOR3_Z, tcurrPathDirAngle));
        t3dRotation = t3dRotation.multiply(Quaternion.RotationAxis(tdirToCam, 0));
        if (!tElement.camIsOnCustomTarget() ||
            this.mRoomManager.interactorState_CameraPath !== InteractorMode.Default ||
            tcurrCustomTarget == null) {
            return;
        }
        if (!tcurrCustomTarget.viewAlongPath) {
            let tIntAlongPath = this.createInteractor("viewAlongPath-interactor", tBoxPos, tPathDir2dperp, -35, t3dRotation, false, true, undefined, "#95C11F");
            tIntAlongPath.type = InteractorType.Button;
            tIntAlongPath.onTriggerAction = () => this.mRoomManager.CustomCamTarget_viewAlong();
        }
    }
    showInteractorsPillar(p3dRotation, pDirP) {
        //
    }
    showInteractorsBackgroundImage(p3dRotationX, p3dRotationX2, pDirP, pShow) {
        let tView = this.mRoomManager.view;
        if (tView == null)
            return;
        // let tElement = (this.mRoomElement.geometry as BackgroundImage);
        let tPoints = this.mRoomElement.Params.ElementPoints.slice(); // tElement.getPoints().slice();
        if (pShow) {
            //move rectangle points
            for (let i = 0; i < tPoints.length; i++) {
                let tTP = Vector3.TransformCoordinates(tPoints[i], this.mRoomElement.transform);
                let tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotationX, false, true);
                tInteractor.onTriggerAction = () => this.changeImageSize(ScaleBackgroundImage.Mode.MovePoint, i, tTP);
            }
            // scale interactor: left; id: -1
            let tPL = Vector3.Center(tPoints[0], tPoints[1]);
            tPL = Vector3.TransformCoordinates(tPL, this.mRoomElement.transform);
            let tInteractorL = this.createInteractor("scale-interactor", tPL, null, 0, p3dRotationX2, false, true);
            tInteractorL.onTriggerAction = () => this.changeImageSize(ScaleBackgroundImage.Mode.ScaleBackgroundImage, -1, tPL);
            // scale interactor: bottom; id: -2
            let tPB = Vector3.Center(tPoints[1], tPoints[2]);
            tPB = Vector3.TransformCoordinates(tPB, this.mRoomElement.transform);
            let tInteractorB = this.createInteractor("scale-interactor", tPB, null, 0, p3dRotationX2, false, true);
            tInteractorB.onTriggerAction = () => this.changeImageSize(ScaleBackgroundImage.Mode.ScaleBackgroundImage, -2, tPB);
            // scale interactor: right; id: -3
            let tPR = Vector3.Center(tPoints[2], tPoints[3]);
            tPR = Vector3.TransformCoordinates(tPR, this.mRoomElement.transform);
            let tInteractorR = this.createInteractor("scale-interactor", tPR, null, 0, p3dRotationX2, false, true);
            tInteractorR.onTriggerAction = () => this.changeImageSize(ScaleBackgroundImage.Mode.ScaleBackgroundImage, -3, tPR);
            // scale interactor: top; id: -4
            let tPT = Vector3.Center(tPoints[0], tPoints[3]);
            tPT = Vector3.TransformCoordinates(tPT, this.mRoomElement.transform);
            let tInteractorT = this.createInteractor("scale-interactor", tPT, null, 0, p3dRotationX2, false, true);
            tInteractorT.onTriggerAction = () => this.changeImageSize(ScaleBackgroundImage.Mode.ScaleBackgroundImage, -4, tPT);
        }
    }
    showInteractorsText(p3dRotationX, p3dRotationX2, pDirP, pShow) {
        let tView = this.mRoomManager.view;
        if (tView == null)
            return;
        let tHW = this.mRoomElement.width * 0.5;
        let tHD = this.mRoomElement.depth * 0.5;
        let tX = new Vector3(tHW, 0, 0);
        let tZ = new Vector3(0, 0, tHD);
        let tDiag = tX.add(tZ);
        let tWM = this.mRoomElement.node.getWorldMatrix();
        let teditPos = Vector3.TransformCoordinates(tDiag, tWM);
        if (pShow) {
            //change text size
            let tInteractor = this.createInteractor("textsize-interactor", teditPos, null, 0, p3dRotationX, false, true);
            tInteractor.onTriggerAction = () => this.resizeText(MoveText.Mode.MoveText, 1);
        }
    }
    showInteractorsCustomBuilding(p3dRotation, pDirP, pShow, pOnlyLength) {
        let tElement = this.mRoomElement.geometry;
        let tPoints = tElement.getPoints().slice();
        if (pOnlyLength) {
            this.showCustomBuildingLengthInteractors();
        }
        else {
            if (pShow) {
                //move line points
                for (let i = 0; i < tPoints.length; i++) {
                    let tP = tPoints[i];
                    let tTP = tP;
                    tTP = Util.to3d(Util.to2d(Vector3.TransformCoordinates(tP, this.mRoomElement.transform)));
                    let tInteractor = this.createInteractor("dot-interactor", tTP, null, 0, p3dRotation);
                    tInteractor.onTriggerAction = () => this.movePointCustomBuilding(MoveCustomBuilding.Mode.MovePoint, i);
                }
            }
            this.showCustomBuildingLengthInteractors();
        }
    }
    showGridSideInteractor(p3dRotationX) {
        let tElement = this.mRoomElement.geometry;
        this.mInteractorManager.enabled = true;
        let tPoints = tElement.getPoints();
        if (tPoints.length < 4)
            return;
        let tP1 = Vector3.TransformCoordinates(tPoints[0], this.mRoomElement.transform);
        let tP2 = Vector3.TransformCoordinates(tPoints[1], this.mRoomElement.transform);
        let tP3 = Vector3.TransformCoordinates(tPoints[3], this.mRoomElement.transform);
        //side interactor
        let tDirT = tP2.subtract(tP1);
        //let tDirPT = utils.math.perpendicularVector3(tDirT);
        let tDirPT = new Vector3(-1, 0, 0);
        let tAngleT = signedAngleBetweenVectors2(VECTOR2_X, Util.to2d(tDirT));
        let t3dRotationT = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngleT);
        t3dRotationT.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        //top interactor
        let tDirS = tP3.subtract(tP1);
        //let tDirPS = utils.math.perpendicularVector3(tDirS);
        let tDirPS = new Vector3(0, 0, 1);
        let tAngleS = signedAngleBetweenVectors2(VECTOR2_X, Util.to2d(tDirS));
        let t3dRotationS = Quaternion.RotationAxis(new Vector3(0, 1, 0), -tAngleS);
        t3dRotationS.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        //side
        let tSideCenterT = Vector3.Center(tP2, tP1);
        let tSideLengthLT = Vector3.Distance(tP2, tP1);
        let tInteractorLT = this.createTextInteractor(tSideCenterT, this.lengthToString(tSideLengthLT) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), tDirT.normalize(), tDirPT.normalize(), 14, t3dRotationT, false, null, true);
        tInteractorLT.onTriggerAction = () => this.showInputField(RoomElementInteractors.RECTANGLE_SIDE, tSideLengthLT, tInteractorLT);
        tInteractorLT.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorLT.element.firstChild);
        tInteractorLT.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorLT.element.firstChild);
        //top
        let tSideCenterS = Vector3.Center(tP3, tP1);
        let tSideLengthLS = Vector3.Distance(tP3, tP1);
        let tInteractorLS = this.createTextInteractor(tSideCenterS, this.lengthToString(tSideLengthLS) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), tDirS.normalize(), tDirPS.normalize(), 14, t3dRotationS, false, null, true);
        tInteractorLS.onTriggerAction = () => this.showInputField(RoomElementInteractors.RECTANGLE_TOP, tSideLengthLS, tInteractorLS);
        tInteractorLS.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractorLS.element.firstChild);
        tInteractorLS.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractorLS.element.firstChild);
    }
    getDistTextPos(pDistIterator, pDistance) {
        let tV1 = this.mRoomElement.getEdge(pDistIterator, false);
        let tV2;
        switch (pDistIterator) {
            case 0:
                tV2 = this.mRoomElement.getEdge(1, false);
                break;
            case 1:
                tV2 = this.mRoomElement.getEdge(0, false);
                break;
            case 2:
                tV2 = this.mRoomElement.getEdge(3, false);
                break;
            default: //3
                tV2 = this.mRoomElement.getEdge(2, false);
                break;
        }
        let tVDir = tV1.subtract(tV2).normalize();
        let tV = tV1.add(tVDir.scaleInPlace(pDistance / 2));
        return (tV);
    }
    movePointLine(pMode, pPointIndex) {
        let tTool = new MoveLine(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    deletePolygonPoint(pPointIndex) {
        //this.mRoomElement.deletePoint(pPointIndex);
        let tidxPoint = this.mRoomElement.Params.ElementPoints[pPointIndex].clone();
        tidxPoint.y = this.mRoomElement.height + tidxPoint.y;
        const tPickPlane = Plane.FromPositionAndNormal(tidxPoint, Vector3.Up());
        const toldPickPlane = this.mRoomElement.getPickPlane().clone();
        let tTool = new ChangePolygonPoints(this.mRoomManager, tidxPoint);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(ChangePolygonPoints.Mode.Delete, pPointIndex, this.mRoomElement, toldPickPlane);
        this.mRoomElement.setPickPlane(tPickPlane);
        this.mApp.tools.startTool(tTool, pPointIndex);
    }
    movePolygonPoint_Height(pPointIndex) {
        this.mRoomElement.setChgPointMode(ChangePolygonPoints.Mode.changeHeight);
        let tidxPoint = this.mRoomElement.Params.ElementPoints[pPointIndex].clone();
        tidxPoint.y = this.mRoomElement.height + tidxPoint.y;
        const tPickPlane = Util.getPickPlane(this.mView, true, tidxPoint);
        const toldPickPlane = this.mRoomElement.getPickPlane().clone();
        let tTool = new ChangePolygonPoints(this.mRoomManager, tidxPoint);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(ChangePolygonPoints.Mode.changeHeight, pPointIndex, this.mRoomElement, toldPickPlane);
        this.mRoomElement.setPickPlane(tPickPlane);
        this.mApp.tools.startTool(tTool, pPointIndex);
    }
    movePolygonPoint_TargetHeight(pCustomTarget) {
        this.mRoomElement.setChgPointMode(ChangePolygonPoints.Mode.changeHeight);
        let tidxPoint = pCustomTarget.target.clone();
        const tPickPlane = Util.getPickPlane(this.mView, true, tidxPoint);
        const toldPickPlane = this.mRoomElement.getPickPlane().clone();
        let tTool = new ChangePolygonPoints(this.mRoomManager, tidxPoint);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(ChangePolygonPoints.Mode.changeHeight, -999, this.mRoomElement, toldPickPlane);
        this.mRoomElement.setPickPlane(tPickPlane);
        this.mApp.tools.startTool(tTool);
    }
    addPolygonPoint(pPointIndex) {
        //this.mRoomElement.addPoint(pPointIndex);
        let tTool = new ChangePolygonPoints(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(ChangePolygonPoints.Mode.Add, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool, pPointIndex);
    }
    extendCamPath(pAddEnding) {
        const tArea2d = new Area2D(this.mRoomManager, "CameraPath");
        tArea2d.initialize(this.mRoomElement, pAddEnding);
        this.mRoomManager.app.tools.startTool(tArea2d);
    }
    movePointSpline(pMode, pPointIndex) {
        let tTool = new MoveSpline(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    moveSplineHill() {
        let tTool = new MoveSplineHill(this.mRoomManager);
        tTool.initialize(this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    moveLineHill() {
        let tTool = new MoveLineHill(this.mRoomManager);
        tTool.initialize(this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    moveRectangleHill() {
        let tTool = new MoveRectangleHill(this.mRoomManager);
        tTool.initialize(this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    movePointRoad(pMode, pPointIndex) {
        let tTool = new MoveRoad(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    movePointHedge(pMode, pPointIndex) {
        let tTool = new MoveHedge(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    movePointFence(pMode, pPointIndex) {
        let tTool = new MoveFence(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    movePointRectangle(pMode, pPointIndex) {
        let tTool = new MoveRectangle(this.mRoomManager);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    movePointGrid(pMode, pPointIndex) {
        let tTool = new MoveGrid(this.mRoomManager);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    resizeText(pMode, pPointIndex) {
        let tTool = new MoveText(this.mRoomManager);
        tTool.initialize(pMode, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    movePointCircle(pMode, pPointIndex) {
        let tTool = new MoveCircle(this.mRoomManager);
        tTool.initialize(pMode, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    moveCircleHill() {
        let tTool = new MoveCircleHill(this.mRoomManager);
        tTool.initialize(this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    toggleSZVisibility() {
        if (this.mRoomElement.type !== "SafetyZone")
            return;
        let tSZvisibility = this.mRoomElement.get_SafetyZone_Visibility();
        this.mRoomElement.set_SafetyZone_Visibility(!tSZvisibility);
        this.mRoomManager.cutOverlappingSZShapes();
        if (this.mRoomElement.node != null) {
            let tPN = this.mRoomElement.node.parent;
            if (tPN != null) {
                let tMA = SceneElement.FromNode(tPN, true);
                if (tMA != null) {
                    this.mApp.model.setSelection([tMA]);
                    this.mApp.model.setSubElementSelection(tMA, this.mRoomElement);
                }
                else {
                    this.mApp.model.setSelection([this.mRoomElement]);
                }
            }
            else {
                this.mApp.model.setSelection([this.mRoomElement]);
            }
        }
        else {
            //
        }
    }
    movePointCustomBuilding(pMode, pPointIndex) {
        let tTool = new MoveCustomBuilding(this.mRoomManager);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement);
        this.mApp.tools.startTool(tTool);
    }
    moveCameraPathPoint(pMode, pPointIndex) {
        let tidxPoint = this.mRoomElement.Params.ElementPoints[pPointIndex].clone();
        tidxPoint.y = this.mRoomElement.height + tidxPoint.y;
        const tPickPlane = Plane.FromPositionAndNormal(tidxPoint, Vector3.Up());
        const toldPickPlane = this.mRoomElement.getPickPlane().clone();
        let tTool = new MoveCameraPath(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement, toldPickPlane);
        this.mRoomElement.setPickPlane(tPickPlane);
        this.mApp.tools.startTool(tTool);
    }
    moveCameraBox(pMode, pPointIndex) {
        const tcamGeo = this.mRoomElement.geometry;
        if (tcamGeo == null) {
            return;
        }
        let tboxPos = tcamGeo.getBoxPos();
        tboxPos = Vector3.TransformCoordinates(tboxPos, this.mRoomElement.transform);
        const tPickPlane = Plane.FromPositionAndNormal(tboxPos, Vector3.Up());
        const toldPickPlane = this.mRoomElement.getPickPlane().clone();
        let tTool = new MoveCameraPath(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement, toldPickPlane);
        this.mRoomElement.setPickPlane(tPickPlane);
        this.mApp.tools.startTool(tTool);
    }
    moveCameraBox2Point(pPointIndex) {
        if (this.mRoomElement.node == null) {
            return;
        }
        const tElemGeo = this.mRoomElement.geometry;
        if (tElemGeo == null) {
            return;
        }
        tElemGeo.setBoxPosByIndex(pPointIndex);
        this.mRoomElement.updateGeometry(false, false);
        this.mApp.model.setSelection([this.mRoomElement]);
    }
    moveCameraView(pMode, pPointIndex) {
        if (this.mRoomElement.type !== "CameraPath") {
            return;
        }
        const tREgeo = this.mRoomElement.geometry;
        if (tREgeo == null) {
            return;
        }
        const ttargPos = tREgeo.getTargetPos();
        const tPickPlane = Plane.FromPositionAndNormal(ttargPos, Vector3.Up());
        const toldPickPlane = this.mRoomElement.getPickPlane().clone();
        let tTool = new MoveCameraPath(this.mRoomManager);
        this.mRoomElement.setToolRunning(true);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement, toldPickPlane);
        this.mRoomElement.setPickPlane(tPickPlane);
        this.mApp.tools.startTool(tTool);
    }
    changeImageSize(pMode, pPointIndex, pPointPosition) {
        let tTool = new ScaleBackgroundImage(this.mRoomManager);
        tTool.initialize(pMode, pPointIndex, this.mRoomElement, pPointPosition);
        this.mApp.tools.startTool(tTool);
    }
    setDistanceEdge(pEdge, pOldDist, pDist) {
        let tDiff = pDist - pOldDist;
        let tNewPos;
        let tWall = this.mRoomElement.wall;
        if (tWall == null)
            return;
        if (pEdge === 0 || pEdge === 3) {
            let tV1 = this.mRoomElement.position.subtract(Util.to3d(tWall.startPoint));
            let tOldLength = tV1.length();
            tV1 = Util.to3d(tWall.endPoint.subtract(tWall.startPoint).normalize());
            tV1.scaleInPlace(tOldLength + tDiff);
            tNewPos = tV1.add(Util.to3d(tWall.startPoint));
        }
        else {
            let tV1 = this.mRoomElement.position.subtract(Util.to3d(tWall.endPoint));
            let tOldLength = tV1.length();
            tV1 = Util.to3d(tWall.startPoint).subtract(Util.to3d(tWall.endPoint)).normalize();
            tV1.scaleInPlace(tOldLength + tDiff);
            tNewPos = tV1.add(Util.to3d(tWall.endPoint));
        }
        let tCmd = new EditRoomElement(this.mRoomManager, this.mRoomElement);
        this.mRoomElement.setPosition(tNewPos);
        this.mApp.commands.executeCommand(tCmd);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
RoomElementInteractors.INPUT_DIST = "WallOpening:distance_edge";
RoomElementInteractors.LINE_LENGTH = "Line:length";
RoomElementInteractors.BUILDING_LENGTH = "Building:length";
RoomElementInteractors.LINE_STARTPOINT = "Line:startPoint";
RoomElementInteractors.LINE_REFPOINT1 = "Line:refPoint1";
RoomElementInteractors.LINE_REFPOINT2 = "Line:refPoint2";
RoomElementInteractors.CIRCLE_RADIUS = "Circle:radius";
RoomElementInteractors.RECTANGLE_TOP = "Rectangle:top";
RoomElementInteractors.RECTANGLE_SIDE = "Rectangle:side";
RoomElementInteractors.ARENA_FIELD_LENGTH = "arena:fieldLength";
RoomElementInteractors.INPUT_TEXT = "Text:text";
